import { AppApiURL, AppDomainURL } from '@app/app.settings';
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import {
  CreatePerformerDto,
  CreatePerformerResponseDto,
  CheckSlugResponseDto,
  UpdatePerformerProfileDto,
  PerformerProfileDto,
  UserPerformerListItemDto,
  UpdatePhotosDto,
  PerformersIndexParams,
  HashtagsResponseDto,
  PhotoVm,
  VideoDto,
  SocialLinkDto,
  SocialPlatform,
  PerformerType
} from '@app/modules/account-modules/performers.model';

export type IPerformerCreatePayload = CreatePerformerDto;
export type IPerformerCreateResponse = CreatePerformerResponseDto;

@Injectable()
export class PerformersService {
  constructor(private http: HttpClient) {}

  createPerformer(payload: CreatePerformerDto): Observable<CreatePerformerResponseDto> {
    const body = this.mapCreatePayloadToApi(payload);
    return this.http
      .post<CreatePerformerResponseDto>(AppApiURL + '/performers', body, {
        withCredentials: true,
        observe: 'response',
        headers: { 'Content-Type': 'application/json' }
      })
      .pipe(
        map((res: HttpResponse<CreatePerformerResponseDto>) => res?.body),
        catchError((err: HttpErrorResponse) => throwError(err.error || err))
      );
  }

  /** Map frontend DTO to API format: platform and performerTypes as strings. */
  private mapCreatePayloadToApi(payload: CreatePerformerDto): Record<string, unknown> {
    const socialLinks = (payload.socialLinks ?? []).map(link => ({
      platform: SocialPlatform[link.platform as number] ?? 'Instagram',
      url: link.url ?? ''
    })).filter(link => link.url);
    const performerTypes = (payload.performerTypes ?? []).map(t => PerformerType[t as number] ?? 'Band');
    const videos = (payload.videos ?? []).map(v => ({
      url: v.url ?? '',
      order: typeof v.order === 'number' ? v.order : 0
    }));
    return {
      city: payload.city,
      name: payload.name ?? '',
      bio: payload.bio ?? undefined,
      socialLinks,
      performerRate: payload.performerRate,
      photoKeys: payload.photoKeys ?? [],
      slug: payload.slug ?? undefined,
      genres: payload.genres ?? [],
      performerTypes,
      videos,
      photoKey: payload.photoKey ?? undefined,
      musicPlaylistUrl: payload.musicPlaylistUrl ?? undefined,
      hashTags: payload.hashTags ?? [],
      bookingAvailableText: payload.bookingAvailableText ?? undefined
    };
  }

  checkSlug(slug: string): Observable<CheckSlugResponseDto> {
    return this.http
      .get<CheckSlugResponseDto>(AppApiURL + '/performers/slug/check', {
        params: { slug },
        withCredentials: true,
        observe: 'response'
      })
      .pipe(
        map((res: HttpResponse<CheckSlugResponseDto>) => res?.body),
        catchError(() => throwError(null))
      );
  }

  getPublicProfileUrl(slugOrId: string): string {
    const clean = this.normalizePerformerIdentifier(slugOrId);
    if (!clean) return '';
    const base =
      typeof window !== 'undefined' && window.location.hostname === 'localhost'
        ? window.location.origin
        : (AppDomainURL || '');
    return `${base}/${clean}`.replace(/\/+/g, '/');
  }

  /** True, если identifier похож на хост (localhost, localhost:8082 и т.п.) — такой не используем как slug в URL. */
  isHostlikeIdentifier(identifier: string): boolean {
    const s = (identifier || '').trim();
    if (!s) return false;
    if (typeof window === 'undefined') return /^localhost(:\d+)?$/i.test(s);
    return s === window.location.host || /^localhost(:\d+)?$/i.test(s);
  }

  /** Из ответа API: musicPlaylist может быть строкой (url) или объектом { url }. */
  private resolveMusicPlaylistUrl(musicPlaylistUrl?: string, musicPlaylist?: string | null | { url?: string }): string | undefined {
    if (musicPlaylistUrl && typeof musicPlaylistUrl === 'string') return musicPlaylistUrl;
    if (typeof musicPlaylist === 'string' && musicPlaylist) return musicPlaylist;
    if (musicPlaylist && typeof musicPlaylist === 'object' && musicPlaylist.url) return musicPlaylist.url;
    return undefined;
  }

  /** Один сегмент для запроса/пути: убирает хост и лишние сегменты из identifier. */
  private normalizePerformerIdentifier(identifier: string): string {
    const s = (identifier || '').trim();
    if (!s) return s;
    const segments = s.replace(/^https?:\/\//, '').split('/').filter(Boolean);
    return segments.length ? segments[segments.length - 1]! : s;
  }

  /** Public profile by identifier (slug или performerId). GET /performers/{identifier}. 404 если нет данных или ошибка. */
  getPerformerPublicBySlug(identifier: string): Observable<PerformerProfileDto> {
    const normalized = this.normalizePerformerIdentifier(identifier);
    const encoded = encodeURIComponent(normalized);
    if (!encoded) {
      return throwError({ status: 404 });
    }
    return this.http
      .get<GetPerformerByIdentifierApiResponse>(AppApiURL + '/performers/' + encoded, {
        withCredentials: true,
        observe: 'response'
      })
      .pipe(
        map((res: HttpResponse<GetPerformerByIdentifierApiResponse>) => {
          const body = res?.body;
          if (!body || typeof body !== 'object' || body.id == null) {
            throw { status: 404 };
          }
          const performerTypes: PerformerType[] = (body.performerTypes ?? []).map((t: number | string) =>
            typeof t === 'number' ? t : (PERFORMER_TYPE_STRING_TO_NUMBER[t] ?? PerformerType.Band)
          );
          const socialLinks: SocialLinkDto[] = (body.socialLinks ?? []).map((l: { platform?: string | number; url?: string }) => ({
            platform: typeof l.platform === 'number' ? l.platform : (SOCIAL_PLATFORM_STRING_TO_NUMBER[l.platform ?? ''] ?? SocialPlatform.Instagram),
            url: l.url ?? ''
          }));
          return {
            id: body.id,
            slug: body.slug,
            name: body.name,
            city: body.city ?? body.cityId,
            bio: body.bio ?? undefined,
            image: body.image,
            photos: body.photos,
            photoKeys: body.photoKeys,
            genres: body.genres ?? [],
            performerTypes,
            videos: body.videos ?? [],
            hashTags: body.hashTags ?? [],
            musicPlaylistUrl: this.resolveMusicPlaylistUrl(body.musicPlaylistUrl, body.musicPlaylist),
            socialLinks,
            videoCount: body.videoCount ?? 0,
            musicAvailable: body.musicAvailable,
            isPublished: body.isPublished
          } as PerformerProfileDto;
        }),
        catchError((err: HttpErrorResponse) => throwError(err?.status === 404 ? { status: 404 } : (err.error || err)))
      );
  }

  getPerformersList(): Observable<UserPerformerListItemDto[]> {
    return this.http
      .get<UserPerformerListItemDto[]>(AppApiURL + '/performers', {
        withCredentials: true,
        observe: 'response'
      })
      .pipe(
        map((res: HttpResponse<UserPerformerListItemDto[]>) => {
          const list = res?.body ?? [];
          return list.map((item: UserPerformerListItemDto & { is_published?: boolean }) => ({
            ...item,
            isPublished: item.isPublished ?? item.is_published ?? false
          }));
        }),
        catchError((err: HttpErrorResponse) => throwError(err.error || err))
      );
  }

  /** Upload photo to buffer for album. Body: FormData with file. Returns key. */
  uploadPhotoToBuffer(file: File): Observable<string> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return this.http
      .post<{ key?: string; photoKey?: string } | string>(
        AppApiURL + '/performers/photo',
        formData,
        {
          withCredentials: true,
          observe: 'response'
        }
      )
      .pipe(
        map((res: HttpResponse<{ key?: string; photoKey?: string } | string>) => {
          const body = res?.body;
          return typeof body === 'string' ? body : (body && (body.key ?? body.photoKey)) || '';
        }),
        catchError((err: HttpErrorResponse) => throwError(err.error || err))
      );
  }

  /** Update main photo. Body: FormData with file. Response 204.
   * Frontend sends the image in the crop proportions chosen by the user (no forced aspect).
   * If the stored result is square, the API is resizing/cropping to square — preserve aspect ratio on the server. */
  updateMainPhoto(performerId: string, file: File): Observable<void> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return this.http
      .post<unknown>(
        AppApiURL + `/performers/${performerId}/photo`,
        formData,
        {
          withCredentials: true,
          observe: 'response'
        }
      )
      .pipe(
        map(() => undefined),
        catchError((err: HttpErrorResponse) => throwError(err.error || err))
      );
  }

  /** Delete performer main photo. */
  deleteMainPhoto(performerId: string): Observable<unknown> {
    return this.http
      .delete<unknown>(AppApiURL + `/performers/${performerId}/photo`, {
        withCredentials: true,
        observe: 'response'
      })
      .pipe(
        map((res: HttpResponse<unknown>) => res?.body),
        catchError((err: HttpErrorResponse) => throwError(err.error || err))
      );
  }

  /** Update additional photos set (keys from buffer). */
  updatePerformerPhotos(performerId: string, picKeys: string[] | null): Observable<unknown> {
    const payload: UpdatePhotosDto = { picKeys };
    return this.http
      .put<unknown>(AppApiURL + `/performers/${performerId}/photos`, payload, {
        withCredentials: true,
        observe: 'response',
        headers: { 'Content-Type': 'application/json' }
      })
      .pipe(
        map((res: HttpResponse<unknown>) => res?.body),
        catchError((err: HttpErrorResponse) => throwError(err.error || err))
      );
  }

  /** Search performers with filters and pagination. */
  getPerformersIndex(params?: PerformersIndexParams): Observable<unknown> {
    return this.http
      .get<unknown>(AppApiURL + '/performers/index', {
        params: params as any,
        withCredentials: true,
        observe: 'response'
      })
      .pipe(
        map((res: HttpResponse<unknown>) => res?.body),
        catchError((err: HttpErrorResponse) => throwError(err.error || err))
      );
  }

  /** Search artist hashtags by text. */
  getHashtags(text: string): Observable<HashtagsResponseDto> {
    return this.http
      .get<HashtagsResponseDto>(AppApiURL + '/performers/hashtags', {
        params: { text },
        withCredentials: true,
        observe: 'response'
      })
      .pipe(
        map((res: HttpResponse<HashtagsResponseDto>) => res?.body),
        catchError((err: HttpErrorResponse) => throwError(err.error || err))
      );
  }

  setPerformerPublished(performerId: string, isPublished: boolean): Observable<unknown> {
    const params = new HttpParams().set('isPublished', String(isPublished));
    return this.http
      .put<unknown>(AppApiURL + `/performers/${performerId}/publishing`, null, {
        params,
        withCredentials: true,
        observe: 'response'
      })
      .pipe(
        map((res: HttpResponse<unknown>) => res?.body),
        catchError((err: HttpErrorResponse) => throwError(err.error || err))
      );
  }

  updatePerformerSlug(performerId: string, slug: string): Observable<unknown> {
    return this.http
      .put<unknown>(AppApiURL + `/performers/${performerId}/slug`, { slug }, {
        withCredentials: true,
        observe: 'response'
      })
      .pipe(
        map((res: HttpResponse<unknown>) => res?.body),
        catchError((err: HttpErrorResponse) => throwError(err.error || err))
      );
  }

  updatePerformer(performerId: string, payload: UpdatePerformerProfileDto): Observable<unknown> {
    const body = this.mapUpdatePayloadToApi(payload);
    return this.http
      .put<unknown>(AppApiURL + `/performers/${performerId}`, body, {
        withCredentials: true,
        observe: 'response',
        headers: { 'Content-Type': 'application/json' }
      })
      .pipe(
        map((res: HttpResponse<unknown>) => res?.body),
        catchError((err: HttpErrorResponse) => throwError(err.error || err))
      );
  }

  /** Map update DTO to API format: platform and performerTypes as strings. Arrays are always sent as arrays (empty [] when no data). */
  private mapUpdatePayloadToApi(payload: UpdatePerformerProfileDto): Record<string, unknown> {
    const socialLinks = (payload.socialLinks ?? []).map(link => ({
      platform: SocialPlatform[link.platform as number] ?? 'Instagram',
      url: link.url ?? ''
    })).filter(link => link.url);
    const performerTypes = (payload.performerTypes ?? []).map(t => PerformerType[t as number] ?? 'Band');
    return {
      name: payload.name ?? '',
      city: payload.city,
      bio: payload.bio ?? undefined,
      socialLinks,
      genres: payload.genres ?? [],
      performerTypes,
      videos: payload.videos ?? [],
      musicPlaylistUrl: payload.musicPlaylistUrl ?? undefined,
      hashTags: payload.hashTags ?? []
    };
  }

  getPerformerProfileById(performerId: string): Observable<PerformerProfileDto> {
    return this.http
      .get<GetPerformerProfileApiResponse>(AppApiURL + `/performers/${performerId}/profile`, {
        withCredentials: true,
        observe: 'response'
      })
      .pipe(
        map((res: HttpResponse<GetPerformerProfileApiResponse>) => {
          const body = res?.body;
          if (!body || typeof body !== 'object') return {} as PerformerProfileDto;
          const performerTypes = (body.performerTypes ?? []).map((t: string) => PERFORMER_TYPE_STRING_TO_NUMBER[t] ?? PerformerType.Band);
          const socialLinks: SocialLinkDto[] = (body.socialLinks ?? []).map((l: { platform?: string; url: string }) => ({
            platform: SOCIAL_PLATFORM_STRING_TO_NUMBER[l.platform ?? ''] ?? SocialPlatform.Instagram,
            url: l.url ?? ''
          }));
          const phoneRaw = body.phoneNumber;
          const bookingPhone =
            phoneRaw &&
            (phoneRaw.number ||
              phoneRaw.hasTelegram ||
              phoneRaw.hasMax ||
              phoneRaw.code)
              ? {
                  code: phoneRaw.code,
                  number: phoneRaw.number ?? '',
                  hasTelegram: !!phoneRaw.hasTelegram,
                  hasMax: !!phoneRaw.hasMax
                }
              : undefined;
          return {
            id: body.id,
            name: body.name,
            city: body.cityId,
            bio: body.bio,
            image: body.image,
            photos: body.photos,
            photoKeys: body.photoKeys,
            genres: body.genres,
            performerTypes,
            videos: body.videos,
            hashTags: body.hashTags,
            musicPlaylistUrl: body.musicPlaylistUrl ?? '',
            socialLinks: socialLinks.length ? socialLinks : [],
            isPublished: body.isPublished,
            phoneImage: body.phoneImage ?? body.contactPhoneImage ?? undefined,
            bookingPhone: bookingPhone ?? undefined
          };
        }),
        catchError((err: HttpErrorResponse) => throwError(err.error || err))
      );
  }
}

/** Ответ GET /performers/{identifier} (slug или id). */
interface GetPerformerByIdentifierApiResponse {
  id?: string;
  slug?: string;
  name?: string;
  city?: number;
  cityId?: number;
  bio?: string | null;
  image?: PhotoVm;
  photos?: PhotoVm[];
  photoKeys?: string[];
  genres?: number[];
  performerTypes?: (number | string)[];
  videos?: VideoDto[];
  hashTags?: string[];
  musicPlaylist?: string | null | { url?: string; ownerId?: string; playlistId?: string; accessHash?: string };
  musicPlaylistUrl?: string;
  socialLinks?: Array<{ platform?: string | number; url?: string }>;
  videoCount?: number;
  musicAvailable?: boolean;
  isPublished?: boolean;
}

/** Backend response shape for GET /performers/:id/profile (camelCase). */
interface GetPerformerProfileApiResponse {
  id?: string;
  slug?: string;
  name?: string;
  cityId?: number;
  bio?: string;
  image?: PhotoVm;
  photos?: Array<PhotoVm & { key?: string }>;
  photoKeys?: string[];
  genres?: number[];
  performerTypes?: string[];
  videos?: VideoDto[];
  hashTags?: string[];
  musicPlaylistUrl?: string;
  socialLinks?: Array<{ platform?: string; url: string }>;
  isPublished?: boolean;
  phoneImage?: PhotoVm;
  contactPhoneImage?: PhotoVm;
  phoneNumber?: {
    code?: string | null;
    number?: string;
    hasTelegram?: boolean;
    hasMax?: boolean;
  };
}

const PERFORMER_TYPE_STRING_TO_NUMBER: Record<string, PerformerType> = {
  Band: PerformerType.Band,
  Solo: PerformerType.Solo,
  Cover: PerformerType.Cover,
  Acoustic: PerformerType.Acoustic,
  Singer: PerformerType.Singer,
  Dj: PerformerType.Dj,
  Instrumentalist: PerformerType.Instrumentalist,
  Performance: PerformerType.Performance
};

const SOCIAL_PLATFORM_STRING_TO_NUMBER: Record<string, SocialPlatform> = {
  Instagram: SocialPlatform.Instagram,
  YouTube: SocialPlatform.YouTube,
  VK: SocialPlatform.VK,
  SoundCloud: SocialPlatform.SoundCloud,
  Spotify: SocialPlatform.Spotify,
  AppleMusic: SocialPlatform.AppleMusic,
  TikTok: SocialPlatform.TikTok,
  Website: SocialPlatform.Website,
  YandexMusic: SocialPlatform.YandexMusic,
  Telegram: SocialPlatform.Telegram
};
